import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import { useApi, useMutation } from '@/hooks/useApi';
import { COVERS_BASE_URL, api } from '@/lib';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type {
  Book,
  BookStatus,
  ConnectorJob,
  ConnectorJobsListResponse,
  Distributor,
  DistributorsListResponse,
} from '@/types/api';

function getDistributorDisplayName(d: { distributor_name?: string; name?: string; code?: string }): string {
  return d.distributor_name || d.name || d.code || '—';
}

function getApprovePlatformKey(d: { code?: string; connector_type?: string }): string {
  const connectorType = (d.connector_type || '').trim().toLowerCase();
  const connectorMap: Record<string, string> = {
    'apple-books': 'apple',
    'google-books': 'google',
    'spotify': 'spotify',
    'sftp': 'sftp',
  };
  if (connectorMap[connectorType]) {
    return connectorMap[connectorType];
  }
  return (d.code || '').trim().toLowerCase();
}

// API response wraps book in a 'book' property
interface BookDetailResponse {
  id: string;
  book: Book;
}

// Map status to workflow step
// PROCESSING happens after VALIDATED when publishing, so it should be at step 4 (Publishing in progress)
const STATUS_TO_STEP: Record<string, number> = {
  'PENDING': 0,
  'PROCESSING': 4, // Publishing in progress (after validation, before published)
  'INGESTED': 1,
  'TRANSFORMED': 2,
  'VALIDATED': 3,
  'PUBLISHED': 4,
  'FEED_UPDATED': 4,
  'PUSHED': 5,
  'PULLED_CONFIRMED': 5,
  'FAILED': -1,
  'CANCELLED': -1,
  'ERROR': -1,
};

const workflowSteps = [
  { name: 'Pending', key: 'PENDING' },
  { name: 'Ingested', key: 'INGESTED' },
  { name: 'Transformed', key: 'TRANSFORMED' },
  { name: 'Validated', key: 'VALIDATED' },
  { name: 'Published', key: 'PUBLISHED' },
  { name: 'Pushed', key: 'PUSHED' },
];

function getStepStatus(stepIndex: number, currentStepIndex: number, bookStatus: BookStatus | null) {
  if (bookStatus === 'FAILED' || bookStatus === 'ERROR' || bookStatus === 'CANCELLED') {
    return stepIndex <= currentStepIndex ? 'error' : 'pending';
  }
  
  // Terminal success states - show current step as completed (green), not current (orange)
  const isTerminalSuccess = bookStatus === 'PUBLISHED' || bookStatus === 'PUSHED' || bookStatus === 'PULLED_CONFIRMED' || bookStatus === 'FEED_UPDATED';
  
  if (stepIndex < currentStepIndex) return 'completed';
  if (stepIndex === currentStepIndex) {
    // If it's a terminal success state, show as completed (green)
    return isTerminalSuccess ? 'completed' : 'current';
  }
  return 'pending';
}

function getStepStyle(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-success-500 border-success-500 text-white';
    case 'current':
      return 'bg-primary-500 border-primary-500 text-white animate-pulse';
    case 'error':
      return 'bg-danger-500 border-danger-500 text-white';
    case 'pending':
      return 'bg-white border-slate-300 text-slate-400';
    default:
      return 'bg-white border-slate-300 text-slate-400';
  }
}

function getStatusBadge(status: BookStatus | null) {
  if (!status) return <Badge variant="slate">Unknown</Badge>;

  switch (status) {
    case 'PUBLISHED':
    case 'PUSHED':
    case 'PULLED_CONFIRMED':
      return <Badge variant="success" size="lg">✓ {status}</Badge>;
    case 'PROCESSING':
      return <Badge variant="primary" size="lg">🔄 PUBLISHING...</Badge>;
    case 'PENDING':
    case 'INGESTED':
    case 'TRANSFORMED':
      return <Badge variant="warning" size="lg">⏳ {status}</Badge>;
    case 'VALIDATED':
    case 'FEED_UPDATED':
      return <Badge variant="primary" size="lg">✓ {status}</Badge>;
    case 'FAILED':
    case 'CANCELLED':
    case 'ERROR':
      return <Badge variant="danger" size="lg">✗ {status}</Badge>;
    default:
      return <Badge variant="slate" size="lg">{status}</Badge>;
  }
}

function getBookCoverUrl(book: Book): string | null {
  const base = COVERS_BASE_URL.replace(/\/$/, '');
  const key = book.cover_key;
  if (key && key !== 'placeholders/default.jpg') {
    return `${base}/${key}`;
  }
  // Fallback: cover from kampus-incoming/staging/{publisher}/{isbn}/
  if (book.publisher_name && book.isbn) {
    return `${base}/staging/${encodeURIComponent(book.publisher_name)}/${book.isbn}`;
  }
  return null;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getConnectorJobSortValue(job: ConnectorJob): number {
  const completedAt = job.completed_at ? Date.parse(job.completed_at) : 0;
  const startedAt = job.started_at ? Date.parse(job.started_at) : 0;
  return Math.max(completedAt || 0, startedAt || 0);
}

function getConnectorStatusVariant(status: ConnectorJob['connector_job_status']): 'success' | 'danger' | 'primary' | 'warning' | 'slate' {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
      return 'primary';
    case 'queued':
      return 'warning';
    default:
      return 'slate';
  }
}

function getConnectorStatusLabel(status: ConnectorJob['connector_job_status']): string {
  switch (status) {
    case 'succeeded':
      return 'Succeeded';
    case 'failed':
      return 'Failed';
    case 'running':
      return 'Running';
    case 'queued':
      return 'Queued';
    default:
      return status;
  }
}

export default function DistBookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Modal states
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCoverPreviewOpen, setIsCoverPreviewOpen] = useState(false);
  const [isEditCoverModalOpen, setIsEditCoverModalOpen] = useState(false);
  const [isRetryDistributorsModalOpen, setIsRetryDistributorsModalOpen] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedFailedDistributorIds, setSelectedFailedDistributorIds] = useState<string[]>([]);
  
  // Error states for mutations
  const [approveError, setApproveError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [retryDistributorsError, setRetryDistributorsError] = useState<string | null>(null);
  const [isRetryingDistributors, setIsRetryingDistributors] = useState(false);

  // Cover edit state
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);

  // Fetch book details - API returns { id, book: {...} }
  const { data: bookResponse, isLoading, error, refetch } = useApi<BookDetailResponse>(
    `distribution/books/${id}`
  );

  // Extract book from response
  const book = bookResponse?.book;

  // Fetch distributors/platforms list
  const { data: distributorsData, isLoading: isLoadingDistributors, refetch: refetchDistributors } = useApi<DistributorsListResponse>(
    'distribution/platforms',
    { immediate: true }
  );

  const distributors = distributorsData?.items || [];

  // Fetch connector jobs for this book so we can surface per-distributor errors centrally.
  const {
    data: connectorJobsData,
    error: connectorJobsError,
    isLoading: isLoadingConnectorJobs,
    refetch: refetchConnectorJobs,
  } = useApi<ConnectorJobsListResponse>(
    `admin/connector-jobs?delivery_id=${encodeURIComponent(id || '')}&page=1&pageSize=100`,
    { immediate: Boolean(id), pollInterval: 10000 }
  );
  
  // State for seeding platforms
  const [isSeedingPlatform, setIsSeedingPlatform] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  // Seed a predefined platform (sftp, apple, google, spotify)
  const seedPlatform = async (type: string) => {
    setIsSeedingPlatform(true);
    setSeedError(null);
    try {
      const response = await fetch(`/api/distribution/platforms/seed/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create platform');
      }
      // Refresh the platforms list
      await refetchDistributors();
    } catch (err) {
      setSeedError(err instanceof Error ? err.message : 'Failed to create platform');
    } finally {
      setIsSeedingPlatform(false);
    }
  };

  // Mutation for deleting a book
  const { mutate: deleteBook, isLoading: isDeleting } = useMutation<
    { success: boolean; message?: string }
  >(`distribution/books/${id}`, 'delete', {
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      navigate('/distribution/books');
    },
  });

  const currentStepIndex = book ? (STATUS_TO_STEP[book.status || ''] ?? 0) : 0;

  const latestDistributorFailures = useMemo(() => {
    const jobs = connectorJobsData?.items || [];
    if (jobs.length === 0) return [];

    const latestByDistributor = new Map<string, ConnectorJob>();
    jobs.forEach((job) => {
      const existing = latestByDistributor.get(job.distributor_id);
      if (!existing || getConnectorJobSortValue(job) >= getConnectorJobSortValue(existing)) {
        latestByDistributor.set(job.distributor_id, job);
      }
    });

    const distributorNameById = new Map<string, string>();
    distributors.forEach((d) => {
      distributorNameById.set(d.id, getDistributorDisplayName(d));
    });

    return Array.from(latestByDistributor.values())
      .filter((job) => job.connector_job_status === 'failed')
      .map((job) => ({
        distributorId: job.distributor_id,
        distributorName: distributorNameById.get(job.distributor_id) || job.distributor_id,
        message: job.last_error || 'Distributor reported a failure without an error message.',
      }))
      .sort((a, b) => a.distributorName.localeCompare(b.distributorName));
  }, [connectorJobsData?.items, distributors]);

  const latestDistributorStatuses = useMemo(() => {
    const jobs = connectorJobsData?.items || [];
    if (jobs.length === 0) return [];

    const latestByDistributor = new Map<string, ConnectorJob>();
    jobs.forEach((job) => {
      const existing = latestByDistributor.get(job.distributor_id);
      if (!existing || getConnectorJobSortValue(job) >= getConnectorJobSortValue(existing)) {
        latestByDistributor.set(job.distributor_id, job);
      }
    });

    const distributorNameById = new Map<string, string>();
    distributors.forEach((d) => {
      distributorNameById.set(d.id, getDistributorDisplayName(d));
    });

    return Array.from(latestByDistributor.values())
      .map((job) => ({
        distributorId: job.distributor_id,
        distributorName: distributorNameById.get(job.distributor_id) || job.distributor_id,
        status: job.connector_job_status,
        lastError: job.last_error,
        startedAt: job.started_at,
        completedAt: job.completed_at,
      }))
      .sort((a, b) => a.distributorName.localeCompare(b.distributorName));
  }, [connectorJobsData?.items, distributors]);

  const distributedToNames = useMemo(() => {
    return latestDistributorStatuses
      .filter((item) => item.status === 'succeeded')
      .map((item) => item.distributorName);
  }, [latestDistributorStatuses]);

  const failedDistributorStatuses = useMemo(
    () => latestDistributorStatuses.filter((item) => item.status === 'failed'),
    [latestDistributorStatuses]
  );

  // Automatically flip VALIDATED -> PENDING when viewing a book detail
  const [hasAutoUpdatedStatus, setHasAutoUpdatedStatus] = useState(false);

  useEffect(() => {
    if (!book || hasAutoUpdatedStatus) return;
    if (book.status !== 'VALIDATED') return;

    let cancelled = false;

    (async () => {
      try {
        await api.post(`distribution/books/${book.id}/status`, {
          json: { status: 'PENDING' as BookStatus },
        });

        if (!cancelled) {
          setHasAutoUpdatedStatus(true);
          await refetch();
        }
      } catch (err) {
        console.warn('Failed to auto-update book status to PENDING', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [book, hasAutoUpdatedStatus, refetch]);

  // Handle platform selection toggle (using platform code) - multi selection
  const togglePlatform = (platformCode: string) => {
    setSelectedPlatforms((prev) => {
      if (prev.includes(platformCode)) {
        return prev.filter((code) => code !== platformCode);
      }
      return [...prev, platformCode];
    });
  };

  // Handle approve & publish
  const handleApproveAndPublish = async () => {
    if (!id || !book) return;
    if (isApproving) return;

    if (!user?.organization_id) {
      setApproveError(
        'Organization is not configured for this user. Please re-login or contact support.'
      );
      return;
    }

    if (selectedPlatforms.length === 0) {
      setApproveError('Please select at least one platform to publish to.');
      return;
    }

    const selectedDistributors = distributors.filter((d) =>
      selectedPlatforms.includes(d.code)
    );
    if (selectedDistributors.length !== selectedPlatforms.length) {
      setApproveError('One or more selected platforms could not be resolved.');
      return;
    }

    const platformKeys = Array.from(
      new Set(
        selectedDistributors
          .map((d) => getApprovePlatformKey(d))
          .filter((key) => key.length > 0)
      )
    );
    if (platformKeys.length === 0) {
      setApproveError('Selected platforms are invalid for publish.');
      return;
    }

    try {
      setIsApproving(true);
      setApproveError(null);

      // Keep book scoped to the user's org without pinning it to a single distributor.
      // Distributor selection should happen per publish request via /approve platforms.
      await api.patch(`distribution/books/${book.id}`, {
        json: {
          organization_id: user.organization_id,
        },
      });

      // Call approve endpoint to actually publish to the selected platform
      // Extended timeout (60s) to handle cold starts on first request
      await api.post(`distribution/books/${book.id}/approve`, {
        json: {
          platforms: platformKeys,
        },
        timeout: 60000,
      });

      // First refetch to get updated status, THEN close the modal
      // This ensures the page shows "Publishing" status immediately when modal closes
      await refetch();
      await refetchConnectorJobs();
      setSelectedPlatforms([]);
      setIsApproveModalOpen(false);
    } catch (err) {
      // Distribution worker may return 409 if this book/platform is already approved (idempotency / duplicate job prevention).
      // Treat that as a successful "already in progress / already approved" and refresh state.
      if (err instanceof ApiError && err.status === 409) {
        // First refetch to get updated status, THEN close the modal
        await refetch();
        await refetchConnectorJobs();
        setSelectedPlatforms([]);
        setIsApproveModalOpen(false);
        return;
      }

      if (
        err instanceof ApiError &&
        err.status === 404 &&
        err.message.toLowerCase().includes('no active publishers found')
      ) {
        setApproveError(
          'Selected platform is not active on the distribution worker yet. Please activate the distributor (or re-create it via seed) and try again.'
        );
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to approve and publish book';
        setApproveError(message);
      }
    } finally {
      setIsApproving(false);
    }
  };

  const openRetryDistributorsModal = () => {
    setRetryDistributorsError(null);
    setSelectedFailedDistributorIds(failedDistributorStatuses.map((item) => item.distributorId));
    setIsRetryDistributorsModalOpen(true);
  };

  const toggleFailedDistributorRetry = (distributorId: string) => {
    setSelectedFailedDistributorIds((prev) => (
      prev.includes(distributorId)
        ? prev.filter((id) => id !== distributorId)
        : [...prev, distributorId]
    ));
  };

  const handleRetryFailedDistributors = async () => {
    if (!book) return;

    if (selectedFailedDistributorIds.length === 0) {
      setRetryDistributorsError('Please select at least one failed distributor to retry.');
      return;
    }

    const selectedDistributors = distributors.filter((d) =>
      selectedFailedDistributorIds.includes(d.id)
    );

    if (selectedDistributors.length !== selectedFailedDistributorIds.length) {
      setRetryDistributorsError(
        'One or more selected failed distributors are no longer available. Refresh and try again.'
      );
      return;
    }

    const platformKeys = Array.from(
      new Set(
        selectedDistributors
          .map((d) => getApprovePlatformKey(d))
          .filter((key) => key.length > 0)
      )
    );

    if (platformKeys.length === 0) {
      setRetryDistributorsError('Selected failed distributors cannot be retried due to invalid platform mapping.');
      return;
    }

    try {
      setRetryDistributorsError(null);
      setIsRetryingDistributors(true);

      await api.post(`distribution/books/${book.id}/approve`, {
        json: {
          platforms: platformKeys,
        },
        timeout: 60000,
      });

      await refetch();
      await refetchConnectorJobs();
      setIsRetryDistributorsModalOpen(false);
      setSelectedFailedDistributorIds([]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await refetch();
        await refetchConnectorJobs();
        setIsRetryDistributorsModalOpen(false);
        setSelectedFailedDistributorIds([]);
        return;
      }

      const message =
        err instanceof Error ? err.message : 'Failed to retry selected distributors';
      setRetryDistributorsError(message);
    } finally {
      setIsRetryingDistributors(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    await deleteBook();
  };

  const handleCoverClick = () => {
    setIsCoverPreviewOpen(true);
  };

  const handleCoverFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setCoverError(null);

    if (!file) {
      setSelectedCoverFile(null);
      if (coverPreviewUrl) {
        URL.revokeObjectURL(coverPreviewUrl);
        setCoverPreviewUrl(null);
      }
      return;
    }

    // Basic client-side size validation (~5MB limit matches backend)
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setCoverError('File is too large. Maximum size is 5MB.');
      setSelectedCoverFile(null);
      if (coverPreviewUrl) {
        URL.revokeObjectURL(coverPreviewUrl);
        setCoverPreviewUrl(null);
      }
      return;
    }

    // Preview URL for the selected image
    if (coverPreviewUrl) {
      URL.revokeObjectURL(coverPreviewUrl);
    }
    const preview = URL.createObjectURL(file);
    setSelectedCoverFile(file);
    setCoverPreviewUrl(preview);
  };

  const handleCloseEditCoverModal = () => {
    setIsEditCoverModalOpen(false);
    setCoverError(null);
    setSelectedCoverFile(null);
    if (coverPreviewUrl) {
      URL.revokeObjectURL(coverPreviewUrl);
      setCoverPreviewUrl(null);
    }
  };

  const handleUploadCover = async () => {
    if (!id || !selectedCoverFile) return;

    setIsUploadingCover(true);
    setCoverError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedCoverFile);

      // POST /distribution/books/:id/cover (proxied to Distribution /admin/books/:id/cover)
      await api.post(`distribution/books/${id}/cover`, {
        body: formData,
      });

      // Refresh book details so the new cover_key is loaded
      await refetch();
      handleCloseEditCoverModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload cover';
      setCoverError(message);
    } finally {
      setIsUploadingCover(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner size="lg" />
        <p className="text-slate-500 mt-4">Loading book details...</p>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/distribution/books"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Books
        </Link>

        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Failed to load book</h3>
            <p className="text-slate-500 mb-4">{error || 'Book not found'}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/distribution/books"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Books
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Cover Image */}
        <div className="flex-shrink-0">
          {getBookCoverUrl(book) ? (
            <button
              type="button"
              onClick={handleCoverClick}
              className="focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-2xl"
              title="Click to view full cover"
            >
              <img
                src={getBookCoverUrl(book)!}
                alt={book.title}
                className="w-40 h-60 rounded-2xl object-cover shadow-lg cursor-pointer"
              />
            </button>
          ) : (
            <div className="w-40 h-60 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-sm">No cover</div>
          )}
        </div>

        {/* Book Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{book.title}</h1>
              {book.subtitle && (
                <p className="text-lg text-slate-500 mt-1">{book.subtitle}</p>
              )}
              <p className="font-mono text-sm text-slate-400 mt-2">ISBN: {book.isbn}</p>
            </div>
            {getStatusBadge(book.status)}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div>
              <p className="text-sm text-slate-500">Publisher</p>
              <p className="font-medium text-slate-800">{book.publisher_name || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Language</p>
              <p className="font-medium text-slate-800">{book.language?.toUpperCase() || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Version</p>
              <p className="font-medium text-slate-800">v{book.version}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Started</p>
              <p className="font-medium text-slate-800">{formatDate(book.started_at)}</p>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">Distributed to:</span>{' '}
              {distributedToNames.length > 0 ? distributedToNames.join(', ') : 'Not distributed yet'}
            </p>
          </div>

        </div>
      </div>

      {/* Approve Error Alert */}
      {approveError && (
        <Card className="border-danger-300 bg-danger-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-danger-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-danger-800">Publish Failed</p>
                <p className="text-sm text-danger-600">{approveError}</p>
              </div>
              <button
                onClick={() => setApproveError(null)}
                className="text-danger-500 hover:text-danger-700"
              >
                <span className="sr-only">Dismiss</span>
                ×
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Centralized per-distributor failures */}
      {(latestDistributorFailures.length > 0 || connectorJobsError) && (
        <Card className="border-danger-300 bg-danger-50">
          <CardHeader
            title="Distributor Errors"
            subtitle="Latest failure for each selected distributor"
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={() => refetchConnectorJobs()}
                isLoading={isLoadingConnectorJobs}
                leftIcon={<ArrowPathIcon className="w-4 h-4" />}
              >
                Refresh
              </Button>
            }
          />
          <CardBody>
            {connectorJobsError ? (
              <p className="text-sm text-danger-700">
                Could not load distributor job errors: {connectorJobsError}
              </p>
            ) : (
              <div className="space-y-3">
                {latestDistributorFailures.map((failure) => (
                  <div
                    key={failure.distributorId}
                    className="rounded-xl border border-danger-200 bg-white p-3"
                  >
                    <p className="text-sm font-semibold text-danger-800">
                      {failure.distributorName}
                    </p>
                    <p className="mt-1 text-sm text-danger-700 break-words">
                      {failure.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Distributor-level delivery visibility */}
      <Card>
        <CardHeader
          title="Distributor Delivery Status"
          subtitle="Current state for each distributor this book was sent to"
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchConnectorJobs()}
              isLoading={isLoadingConnectorJobs}
              leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            >
              Refresh
            </Button>
          }
        />
        <CardBody>
          {connectorJobsError ? (
            <p className="text-sm text-danger-700">
              Could not load distributor statuses: {connectorJobsError}
            </p>
          ) : latestDistributorStatuses.length === 0 ? (
            <p className="text-sm text-slate-500">
              No distributor jobs found yet for this book.
            </p>
          ) : (
            <div className="space-y-3">
              {latestDistributorStatuses.map((item) => (
                <div
                  key={item.distributorId}
                  className="rounded-xl border border-slate-200 bg-white p-4"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{item.distributorName}</p>
                      <p className="text-xs text-slate-500">
                        Started: {formatDate(item.startedAt)} · Completed: {formatDate(item.completedAt)}
                      </p>
                    </div>
                    <Badge variant={getConnectorStatusVariant(item.status)} size="md">
                      {getConnectorStatusLabel(item.status)}
                    </Badge>
                  </div>
                  {item.status === 'failed' && item.lastError && (
                    <p className="mt-2 text-sm text-danger-700 break-words">{item.lastError}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Workflow Status */}
      <Card>
        <CardHeader title="Workflow Status" />
        <CardBody>
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {workflowSteps.map((step, index) => {
              const stepStatus = getStepStatus(index, currentStepIndex, book.status);
              return (
                <div key={step.name} className="flex items-center flex-shrink-0">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${getStepStyle(stepStatus)}`}
                    >
                      {stepStatus === 'completed' ? (
                        <CheckIcon className="w-5 h-5" />
                      ) : stepStatus === 'error' ? (
                        <span className="text-sm font-bold">!</span>
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-700 mt-2 whitespace-nowrap">{step.name}</p>
                  </div>
                  {index < workflowSteps.length - 1 && (
                    <div
                      className={`w-8 md:w-16 h-0.5 mx-2 ${
                        stepStatus === 'completed' ? 'bg-success-500' : 
                        stepStatus === 'error' ? 'bg-danger-500' : 'bg-slate-200'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Book Details */}
      <Card>
        <CardHeader title="Book Details" />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500">Book ID</p>
              <p className="font-mono text-sm text-slate-800 break-all">{book.id}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Publisher ID</p>
              <p className="font-mono text-sm text-slate-800 break-all">{book.publisher_id || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Started At</p>
              <p className="text-slate-800">{formatDate(book.started_at)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Completed At</p>
              <p className="text-slate-800">{formatDate(book.completed_at)}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Event History (visible but disabled - feature not yet implemented) */}
      <Card className="opacity-60 pointer-events-none">
        <CardHeader title="Event History" subtitle="Coming soon" />
        <CardBody>
          <p className="text-slate-400 text-center py-4 text-sm">
            Event history is not available yet. This section is currently disabled.
          </p>
        </CardBody>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button 
          leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          variant="outline"
          onClick={() => refetch()}
        >
          Refresh
        </Button>
        {(book.status === 'VALIDATED' ||
          book.status === 'PENDING' ||
          book.status === 'PROCESSING' ||
          book.status === 'PUBLISHED' ||
          book.status === 'PUSHED' ||
          book.status === 'PULLED_CONFIRMED' ||
          book.status === 'FEED_UPDATED') && (
          <Button 
            leftIcon={<CheckIcon className="w-4 h-4" />}
            onClick={() => setIsApproveModalOpen(true)}
          >
            Approve & Publish
          </Button>
        )}
        {failedDistributorStatuses.length > 0 && (
          <Button 
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={openRetryDistributorsModal}
            disabled={isRetryingDistributors}
          >
            Retry Failed Distributors
          </Button>
        )}
        <Button
          variant="outline"
          leftIcon={<PencilIcon className="w-4 h-4" />}
          onClick={() => {
            setCoverError(null);
            setSelectedCoverFile(null);
            if (coverPreviewUrl) {
              URL.revokeObjectURL(coverPreviewUrl);
              setCoverPreviewUrl(null);
            }
            setIsEditCoverModalOpen(true);
          }}
        >
          Edit
        </Button>
        <Button 
          variant="danger" 
          leftIcon={<TrashIcon className="w-4 h-4" />}
          onClick={() => setIsDeleteModalOpen(true)}
        >
          Delete
        </Button>
      </div>

      {/* Approve & Publish Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => {
          // Prevent closing modal while publishing is in progress
          if (isApproving) return;
          setIsApproveModalOpen(false);
          setSelectedPlatforms([]);
        }}
        title="Approve & Publish"
        description={isApproving ? "Publishing in progress, please wait..." : "Select the distributors/platforms to publish this book to"}
        size="md"
      >
        <ModalBody>
          {isApproving ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <Spinner size="lg" />
              <div className="text-center">
                <p className="font-medium text-slate-700">Publishing to platform...</p>
                <p className="text-sm text-slate-500 mt-1">This may take a few seconds. Please wait.</p>
              </div>
            </div>
          ) : isLoadingDistributors ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : distributors.length === 0 ? (
            <div className="space-y-4 py-4">
              <p className="text-slate-500 text-center">No distribution platforms configured yet.</p>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Quick Setup - Add a platform:</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => seedPlatform('sftp')}
                    disabled={isSeedingPlatform}
                    isLoading={isSeedingPlatform}
                  >
                    📁 Add SFTP
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => seedPlatform('apple')}
                    disabled={isSeedingPlatform}
                    isLoading={isSeedingPlatform}
                  >
                    🍎 Add Apple Books
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => seedPlatform('google')}
                    disabled={isSeedingPlatform}
                    isLoading={isSeedingPlatform}
                  >
                    ▶️ Add Google Books
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => seedPlatform('spotify')}
                    disabled={isSeedingPlatform}
                    isLoading={isSeedingPlatform}
                  >
                    🎵 Add Spotify
                  </Button>
                </div>
                {seedError && (
                  <p className="text-sm text-danger-600 mt-2">{seedError}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {distributors.map((distributor: Distributor) => {
                // Get icon for connector type
                const connectorIcons: Record<string, string> = {
                  'apple-books': '🍎',
                  'google-books': '▶️',
                  'sftp': '📁',
                  'spotify': '🎵',
                };
                const ct = (distributor.connector_type || '').trim().toLowerCase();
                const icon = connectorIcons[ct] || '📦';
                
                return (
                  <button
                    key={distributor.id}
                    onClick={() => togglePlatform(distributor.code)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                      selectedPlatforms.includes(distributor.code)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${
                      selectedPlatforms.includes(distributor.code)
                        ? 'bg-primary-500'
                        : 'bg-slate-100'
                    }`}>
                      {icon}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-slate-800">{getDistributorDisplayName(distributor)}</p>
                      <p className="text-sm text-slate-500">
                        {distributor.code} • {distributor.connector_type} • {distributor.mode}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlatforms.includes(distributor.code)
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-slate-300'
                    }`}>
                      {selectedPlatforms.includes(distributor.code) && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsApproveModalOpen(false);
              setSelectedPlatforms([]);
            }}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveAndPublish}
            disabled={selectedPlatforms.length === 0 || isApproving}
            isLoading={isApproving}
            leftIcon={!isApproving ? <CheckIcon className="w-4 h-4" /> : undefined}
          >
            {isApproving
              ? 'Publishing...'
              : `Publish to ${selectedPlatforms.length || 0} Platform${selectedPlatforms.length === 1 ? '' : 's'}`}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Retry Failed Distributors Modal */}
      <Modal
        isOpen={isRetryDistributorsModalOpen}
        onClose={() => {
          if (isRetryingDistributors) return;
          setIsRetryDistributorsModalOpen(false);
          setRetryDistributorsError(null);
          setSelectedFailedDistributorIds([]);
        }}
        title="Retry Failed Distributors"
        description="Select which failed distributors you want to retry now."
        size="md"
      >
        <ModalBody>
          {failedDistributorStatuses.length === 0 ? (
            <p className="text-sm text-slate-500">No failed distributors available to retry.</p>
          ) : (
            <div className="space-y-2">
              {failedDistributorStatuses.map((item) => (
                <button
                  key={item.distributorId}
                  type="button"
                  onClick={() => toggleFailedDistributorRetry(item.distributorId)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    selectedFailedDistributorIds.includes(item.distributorId)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedFailedDistributorIds.includes(item.distributorId)
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {selectedFailedDistributorIds.includes(item.distributorId) && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-slate-800">{item.distributorName}</p>
                    {item.lastError && (
                      <p className="text-sm text-danger-600 mt-1 break-words">{item.lastError}</p>
                    )}
                  </div>
                  <Badge variant="danger" size="sm">Failed</Badge>
                </button>
              ))}
            </div>
          )}
          {retryDistributorsError && (
            <p className="mt-3 text-sm text-danger-600">{retryDistributorsError}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsRetryDistributorsModalOpen(false);
              setRetryDistributorsError(null);
              setSelectedFailedDistributorIds([]);
            }}
            disabled={isRetryingDistributors}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRetryFailedDistributors}
            disabled={selectedFailedDistributorIds.length === 0 || isRetryingDistributors}
            isLoading={isRetryingDistributors}
            leftIcon={!isRetryingDistributors ? <ArrowPathIcon className="w-4 h-4" /> : undefined}
          >
            {isRetryingDistributors
              ? 'Retrying...'
              : `Retry ${selectedFailedDistributorIds.length || 0} Distributor${selectedFailedDistributorIds.length === 1 ? '' : 's'}`}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cover Preview Modal */}
      <Modal
        isOpen={isCoverPreviewOpen}
        onClose={() => setIsCoverPreviewOpen(false)}
        title="Cover preview"
        size="md"
      >
        <ModalBody>
          <div className="flex flex-col items-center gap-4">
            {getBookCoverUrl(book) ? (
              <>
                <img
                  src={getBookCoverUrl(book)!}
                  alt={book.title}
                  className="max-h-[70vh] rounded-2xl shadow-lg"
                />
                {COVERS_BASE_URL && (
                  <a
                    href={getBookCoverUrl(book)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    Open full image in new tab
                  </a>
                )}
              </>
            ) : (
              <p className="text-slate-500">No cover image</p>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Edit Cover Modal */}
      <Modal
        isOpen={isEditCoverModalOpen}
        onClose={handleCloseEditCoverModal}
        title="Edit book cover"
        description="Upload a new cover image for this book"
        size="sm"
      >
        <ModalBody>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center gap-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Current
                </p>
                {getBookCoverUrl(book) ? (
                  <img
                    src={getBookCoverUrl(book)!}
                    alt={book.title}
                    className="w-24 h-36 rounded-xl object-cover shadow-sm"
                  />
                ) : (
                  <div className="w-24 h-36 rounded-xl border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
                    No cover
                  </div>
                )}
              </div>
              <div className="flex flex-col items-center gap-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  New (preview)
                </p>
                {coverPreviewUrl ? (
                  <img
                    src={coverPreviewUrl}
                    alt="New cover preview"
                    className="w-24 h-36 rounded-xl object-cover shadow-sm border border-primary-200"
                  />
                ) : (
                  <div className="w-24 h-36 rounded-xl border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400">
                    No file selected
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Choose new cover image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverFileChange}
                className="mt-2 block w-full text-sm text-slate-600
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-50 file:text-primary-700
                  hover:file:bg-primary-100"
              />
              <p className="mt-1 text-xs text-slate-400">
                Supported formats: JPG, PNG, WebP. Maximum size 5MB.
              </p>
              {coverError && (
                <p className="mt-2 text-sm text-danger-600">{coverError}</p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={handleCloseEditCoverModal}
            disabled={isUploadingCover}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadCover}
            disabled={!selectedCoverFile || isUploadingCover}
            isLoading={isUploadingCover}
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          >
            Save Cover
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Book"
        description="Are you sure you want to delete this book? This action cannot be undone."
        size="sm"
      >
        <ModalBody>
          <div className="flex items-center gap-4 p-4 bg-danger-50 rounded-xl">
            <ExclamationTriangleIcon className="w-10 h-10 text-danger-500" />
            <div>
              <p className="font-medium text-slate-800">{book.title}</p>
              <p className="text-sm text-slate-500">ISBN: {book.isbn}</p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={isDeleting}
            leftIcon={<TrashIcon className="w-4 h-4" />}
          >
            Delete Book
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
